import { useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Card, Col, Image, Result, Row, Typography } from 'antd';
import Layout, { Content, Header } from 'antd/lib/layout/layout';
import { Helmet } from 'react-helmet';
import { useTranslation } from 'react-i18next';
import { useAppDispatch } from 'app/hooks';
import { useLazyGetLoggerOverviewQuery } from 'app/services/loggers';
import { actions as loggerAccess } from 'app/services/loggers/slice';
import { Paths } from 'constants/index';
import Language from 'containers/Language/Language';
import { useCountdown, useScreenBreakpoint } from 'hooks';
import VerificationCodeInput from './components/VerificationCodeInput';
import 'pages/Login/styles.less';
import './overview.less';

const VerificationCode = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { barcode = '' } = useParams<{ barcode: string }>();
  const { isMobile } = useScreenBreakpoint();
  const [ code, setCode ] = useState('');
  const [ status, setStatus ] = useState<number | string | undefined>();
  const verifying = useRef(false);

  const [ getOverview, { isFetching } ] = useLazyGetLoggerOverviewQuery();
  const [ waitSeconds, startWait ] = useCountdown();

  const handleChange = (value: string) => {
    setCode(value);
    if (status && value) setStatus(undefined);
  };

  const handleVerify = async (value: string) => {
    setCode(value);

    if (verifying.current || waitSeconds > 0) return;
    verifying.current = true;

    try {
      const overview = await getOverview({ barcode, code: value }).unwrap();
      dispatch(loggerAccess.grant({ barcode, code: value, overview }));
      navigate(Paths.LOGGERS_OVERVIEW.replace(':barcode', encodeURIComponent(barcode)), { replace: true });
    } catch (error) {
      const failure = error as { status?: number | string, data?: { retryAfterSeconds?: number } } | undefined;
      setStatus(failure?.status);
      if (failure?.status === 429) startWait(failure.data?.retryAfterSeconds ?? 0);
    } finally {
      verifying.current = false;
    }
  };

  const CardTitle = (
    <Row className='title' justify='center'>
      <Col>
        <Image width={200} className='login-logo' preview={false} src={process.env.PUBLIC_URL + '/logo.png'} />
      </Col>
    </Row>
  );

  return (
    <Layout className="login logger-code">
      <Helmet title={`Kvalilog > ${t('pages.loggers.code.title')}`} />
      <Header className="layout-header layout-header-login">
        <Row justify="end">
          <Col>
            <Language />
          </Col>
        </Row>
      </Header>
      <Content>
        <Row className='container' justify="center" align="middle">
          <Col>
            <Card className='card' title={CardTitle} bordered={!isMobile}>
              {status === 404
                ? <Result status="404" title={t('pages.loggers.overviewnotfound')} />
                : (
                  <>
                    <Typography.Paragraph className="code-explanation">
                      {t('pages.loggers.code.explanation')}
                    </Typography.Paragraph>
                    <VerificationCodeInput
                      disabled={isFetching || waitSeconds > 0}
                      hasError={status === 400 || status === 429}
                      onChange={handleChange}
                      onComplete={handleVerify}
                    />
                    {waitSeconds > 0 && (
                      <Typography.Paragraph className="code-waiting" type="danger">
                        {t('pages.loggers.code.waiting', { seconds: waitSeconds })}
                      </Typography.Paragraph>
                    )}
                    <Button
                      type="primary"
                      size="large"
                      className="login-button"
                      loading={isFetching}
                      disabled={code.length < 4 || waitSeconds > 0}
                      onClick={() => handleVerify(code)}
                    >
                      {t('pages.loggers.code.submit')}
                    </Button>
                  </>
                )}
            </Card>
          </Col>
        </Row>
      </Content>
    </Layout>
  );
};

export default VerificationCode;
